import { ImageName } from "@mcleod/images";
import { ChangeEvent, Checkbox, ClickEvent, DataSourceMode, Image, Label, Layout, SaveButton, SlideoutDecorator, Snackbar, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { Model, ModelRow } from "@mcleod/core";
import { getDispatchControlBoolean } from "@mcleod/general";
import { AutogenLayoutMovementRollback } from "./autogen/AutogenLayoutMovementRollback";
import { UnassignDialog } from "./UnassignDialog";
import { OrderTabView } from "./OrderTabView";
import { DetentionUtil } from "./DetentionUtil";

export interface RBAfterSaveProps {
    cancelled: boolean;
    currentOrderId: string;
    currentMoveId: string;
    orderId: string;
    nextAvailableMoveId?: string;
}

export class MovementRollback extends AutogenLayoutMovementRollback {
    private saveButton: SaveButton = null;
    private isLoaded = true;

    public static showMovementRollbackSlideout(movement:ModelRow, doAfterSave: (props:RBAfterSaveProps) => void) {
        const movementId = movement.get("id");
        Model.searchSingleRecord("lme/dispatch/unclear-stops", {"movement_id": movementId}).then((response) => {
            const layout = Layout.getLayout("lme/dispatch/MovementRollback", {
                minWidth: 1200, fillHeight: true, padding: 8
            }) as MovementRollback;
            layout.init(movement, response, doAfterSave);
            new SlideoutDecorator({
                layout: layout,
                title: `Rollback Movement - Movement ${movement.get("id")}`,
                overlayProps: {width: "60%", height: "60%", closeOnClickOff: false, greyedBackground: true},
                fillVerticalSpace: true,
                addlComponents: layout.saveButton
            });
        }).catch(error => {
            Snackbar.showWarningSnackbar(error.messages[0]);
        });
    }

    public init(movement:ModelRow, modelRow:ModelRow, doAfterSave: (props:RBAfterSaveProps) => void) {
        modelRow._appending = true;
        const data = [modelRow];
        this.getSaveButton(doAfterSave);

        this.addLayoutLoadListener(async (event) => {
            this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, data, null);
            await this.sourceStop.search({ "movement_id": movement.get("id") });
            this.activeRow.getMetadata().keyFields = ["movement_id"];
            this.activeRow.set("movement_id", movement.get("id"));
            this.handleWarnings();
            this.saveButton.enabled = true;
            this.isLoaded = movement.getString("loaded", "L") === "L";

            // Technically, the following code doesn't matter.  If the trailer is not left preassigned,
            // then the tractor will be removed regardless of what the user selects.  This is just to
            // reflect that in the UI.  We may change this in a future card.
            this.activeRow.addAfterFieldUpdateListener((event) => {
               if (event.fieldName == "leave_trailer_assigned" && !event.newValue) {
                   this.activeRow.set("leave_tractor_assigned", false);
               } else if (event.fieldName == "leave_tractor_assigned" && event.newValue) {
                   this.activeRow.set("leave_trailer_assigned", true);
               }
            });

            this.labelInstructEmpty.visible = !this.isLoaded;
            this.labelInstructAll.visible = this.isLoaded;
            this.labelInstructPartial.visible = this.isLoaded;
            this.panelOptions.visible = this.isLoaded;
            this.switchYSplit.visible = this.activeRow.getBoolean("is_split_move", false);
        })
    }

    labelOrderIdOnClick(event: ClickEvent) {
        const txtOrderId = event.target as Label;
        const orderId = txtOrderId.boundRow?.get("order_id");
        txtOrderId.addUnderlineWhenMouseOver();
        txtOrderId.addClickListener((event: ClickEvent) => { OrderTabView.navigateTo({ key: orderId, newTab: true }); });
    }

    private getSaveButton(doAfterSave: (props:RBAfterSaveProps)=>void) {
        this.saveButton = SaveButton.create({
            id: "saveButton",
            caption: "Save",
            rowBreak: false,
            enabled: false,
            width: 75,
            backgroundColor: "primary",
            color: "primary.reverse",
            borderWidth: 0,
            onClick: async (event: ClickEvent) => {
                this.saveButton.busy = true;
                if (!this.activeRow.getBoolean("leave_tractor_assigned", true) && getDispatchControlBoolean("req_unassign_reason", false)) {
                    const reason = await new UnassignDialog("B").show();
                    if (reason) {
                        this.activeRow.set("unassign_reason_code", reason.get("reason_code"));
                        this.activeRow.set("unassign_reason_comments", reason.get("comments"));
                    } else {
                        this.saveButton.busy = false;
                        return;
                    }
                }
                this.sourceUnclearStops.activeRow.set("order_id");
                this.sourceUnclearStops.post().then(_ => {
                    this.slideOut().then(() => doAfterSave({
                        cancelled: false,
                        currentOrderId: this.sourceUnclearStops.activeRow.get("current_order_id"),
                        currentMoveId: this.sourceUnclearStops.activeRow.get("current_move_id"),
                        orderId: this.sourceUnclearStops.activeRow.get("order_id"),
                        nextAvailableMoveId: this.sourceUnclearStops.activeRow.get("movement_id")
                    }));
                }).finally(() => {
                    this.saveButton.busy = false;
                });
            }
        });
    }

    private handleWarnings() {
        const warnings = this.activeRow.get("warnings");
        const hasWarnings = warnings != null && warnings.length > 0;

        this.panelWarningsContainer.visible = hasWarnings;
        if (hasWarnings) {
            warnings.forEach((warning:string) => {
                const label = new Label({ text: warning, color: "warning", fontSize: "large", rowBreak: true });
                this.panelWarnings.add(label);
            })
        }
    }

    private toggleCheckboxes(rowIndex:number, newValue:boolean) {
        const fullRollback = newValue && rowIndex == 0;
        let allUnchecked = true;
        /* Cancel/Rollback Types:
            0 - Cancel
            1 - Cancel to Covered
            2 - Cancel to Progress
            3 - Cancel to Void
        */
        if (fullRollback) this.activeRow.set("stop_id", null);
        this.labelLocateInfo.visible = fullRollback;
        this.activeRow.set("cancel_type", fullRollback ? 0 : 2);
        this.activeRow.set("leave_tractor_assigned", true);
        this.switchLeaveTractor.enabled = fullRollback;
        this.activeRow.set("leave_trailer_assigned", true);
        this.switchLeaveTrailer.enabled = fullRollback;
        this.activeRow.set("leave_chassis_assigned", true);
        this.switchLeaveChassis.enabled = fullRollback;

        this.tableStops.allRows.forEach((row, index) => {
            const data = row.data as ModelRow;

            let checked = true;
            if (fullRollback) {
                allUnchecked = false;
            } else {
                if (index == rowIndex && newValue) {
                    this.sourceUnclearStops.activeRow.set("stop_id", data.get("id"));
                    allUnchecked = false;
                } else if (index < rowIndex) {
                    checked = false;
                } else if (index > rowIndex) {
                    if (!data.isNull("actual_arrival")) {
                        if (!newValue && index == rowIndex + 1)
                            this.sourceUnclearStops.activeRow.set("stop_id", data.get("id"));
                        allUnchecked = false;
                    }
                } else {
                    checked = newValue;
                }
            }
            if (row.populatedDOM) {
                const chSelected = row.findComponentById("checkboxSelected") as Checkbox;
                if (chSelected.visible)
                    chSelected.checked = checked;
            }
        });

        // prevent save if no stops are selected
        if (allUnchecked) {
            this.saveButton.disabledTooltip = "At least one stop must be selected for rollback.";
            this.saveButton.enabled = false;
        } else {
            this.saveButton.enabled = true;
        }
    }

    tableStopsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        const isPickup = modelRow.getBoolean("is_pickup", true);
        const imageStopType = tableRow.findComponentById("imageStopType") as Image;
        const labelStopType = tableRow.findComponentById("labelStopType") as Label;
        const stopType = modelRow.get("stop_type");

        imageStopType.color = stopType === "SP" ? "success" : "primary";
        if (isPickup) {
            if (stopType === "SP") {
                labelStopType.text = "SPLIT PICKUP";
                imageStopType.name = ImageName.ysplitPick;
            } else {
                labelStopType.text = "PICKUP";
                imageStopType.name = ImageName.loadPickup;
            }
        } else {
            if (stopType === "SD") {
                labelStopType.text = "SPLIT DROP";
                imageStopType.name = ImageName.ysplit;
            } else {
                labelStopType.text = "DELIVERY";
                imageStopType.name = ImageName.loadDelivery;
            }
        }

        const arrived = !modelRow.isNull("actual_arrival");
        if ("A" === modelRow.get("status") && modelRow.get("actual_arrival") != null) {
            tableRow.findComponentById("labelArrived").visible = true;
        } else if ("D" === modelRow.get("status")) {
            tableRow.findComponentById("labelDeparted").visible = true;
        }// default visibility are both false

        const chSelected = tableRow.findComponentById("checkboxSelected") as Checkbox;
        chSelected.checked = arrived;
        chSelected.visible = arrived;
        if (!this.isLoaded) chSelected.visible = false;
        const imageDetention = tableRow.findComponentById("imageDetention") as Image;
        DetentionUtil.checkStopDetention(modelRow, "detention_hist.status", imageDetention);
    }

    checkboxSelectedOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const checkbox = event.target as Checkbox;
            const tableRow = TableRow.getContainingTableRow(checkbox);
            this.toggleCheckboxes(tableRow.index, event.newValue as boolean);
        }
    }
}
